import { SoundFiles } from '../../assets/sounds/Map';
import { getRandomElement } from 'shared';

type Sound = HTMLAudioElement;

type TrackPlayer = () => void;

export interface ISoundPlayer {
  click: TrackPlayer;
  queueMatch: TrackPlayer;
  cardClick: TrackPlayer;
  cashReceive: TrackPlayer;
  gotCash: TrackPlayer;
  rankUpgrade: TrackPlayer;
  battleCry: TrackPlayer;
  battleMove: TrackPlayer;
  round: {
    win: TrackPlayer;
    lose: TrackPlayer;
    draw: TrackPlayer;
  };
  fight: {
    win: TrackPlayer;
    lose: TrackPlayer;
    draw: TrackPlayer;
  };
}

export class SoundPlayer implements ISoundPlayer {
  public readonly click: TrackPlayer;
  public readonly queueMatch: TrackPlayer;
  public readonly cardClick: TrackPlayer;
  public readonly cashReceive: TrackPlayer;
  public readonly gotCash: TrackPlayer;
  public readonly rankUpgrade: TrackPlayer;
  public readonly battleCry: TrackPlayer;
  public readonly battleMove: TrackPlayer;
  public readonly round: {
    win: TrackPlayer;
    lose: TrackPlayer;
    draw: TrackPlayer;
  };
  public readonly fight: {
    win: TrackPlayer;
    lose: TrackPlayer;
    draw: TrackPlayer;
  };

  private currentlyPlaying: Sound | null = null;

  constructor() {
    this.click = this.generatePlayer(SoundFiles.CLICK);
    this.queueMatch = this.generatePlayer(SoundFiles.QUEUE_MATCH);
    this.cardClick = this.generatePlayer(SoundFiles.CARD_CLICK);
    this.cashReceive = this.generatePlayer(SoundFiles.CASH_RECEIVE);
    this.gotCash = this.generatePlayer(SoundFiles.GOT_CASH);
    this.rankUpgrade = this.generatePlayer(SoundFiles.RANK_UPGRADE);
    this.round = {
      win: this.generatePlayer(SoundFiles.ROUND_WIN),
      lose: this.generatePlayer(SoundFiles.ROUND_LOSE),
      draw: this.generatePlayer(SoundFiles.ROUND_DRAW)
    };
    this.fight = {
      win: this.generatePlayer(SoundFiles.FIGHT_WIN),
      lose: this.generatePlayer(SoundFiles.FIGHT_LOSE),
      draw: this.generatePlayer(SoundFiles.FIGHT_DRAW)
    };
    this.battleCry = this.generateRandomPlayer([
      SoundFiles.BATTLECRY_1,
      SoundFiles.BATTLECRY_2,
      SoundFiles.BATTLECRY_3,
      SoundFiles.BATTLECRY_4,
      SoundFiles.BATTLECRY_5
    ]);
    this.battleMove = this.generateRandomPlayer([
      SoundFiles.MOVE_1,
      SoundFiles.MOVE_2,
      SoundFiles.MOVE_3,
      SoundFiles.MOVE_4,
      SoundFiles.MOVE_5
    ]);
  }

  private buildAudio = (fileIndex: SoundFiles): Sound => {
    const audio = new Audio(`/assets/sounds/${fileIndex}.wav`);
    audio.preload = 'auto';
    audio.load();
    return audio;
  };

  private playSound = (sound: Sound) => {
    if (!sound) return;
    if (this.currentlyPlaying) {
      this.currentlyPlaying.pause();
      this.currentlyPlaying.currentTime = 0;
    }
    this.currentlyPlaying = sound;
    this.currentlyPlaying.play().catch(() => {});
  };

  private generatePlayer = (sourceName: SoundFiles): TrackPlayer => {
    const sound = this.buildAudio(sourceName);
    return () => this.playSound(sound);
  };

  private generateRandomPlayer = (sources: SoundFiles[]): TrackPlayer => {
    const sounds = sources.map((source) => this.buildAudio(source));
    return () => this.playSound(getRandomElement(sounds));
  };
}

export const fakeSoundPlayer: ISoundPlayer = {
  click: () => {},
  queueMatch: () => {},
  cardClick: () => {},
  cashReceive: () => {},
  gotCash: () => {},
  rankUpgrade: () => {},
  battleCry: () => {},
  battleMove: () => {},
  round: {
    win: () => {},
    lose: () => {},
    draw: () => {}
  },
  fight: {
    win: () => {},
    lose: () => {},
    draw: () => {}
  }
};
